import { ApiServices } from "~api/services";
import {
  GetPolizaCargaPublicaResponse,
  GetSiniestroResponse,
} from "~api/types";
import type { FieldChangeEvent } from "~business/Cards/types";

/**
 * Los dos formularios de carga —el público y el interno— manejan los mismos
 * datos, así que comparten estos tipos. Se definen una sola vez para que no
 * diverjan: ese es el motivo por el que ambos se migraron juntos.
 */

/**
 * Campo de formulario: el valor que se muestra, si pasó las validaciones, y si
 * le toca tomar el foco cuando se intenta guardar con errores.
 */
export interface CampoSiniestro<T = string> {
  value: T;
  valid: boolean;
  focus: boolean;
}

/**
 * La provincia guarda además el id con el que se piden localidades y códigos
 * postales: `value` es el nombre que se muestra, `searchId` el id del catálogo.
 */
export interface CampoProvincia extends CampoSiniestro {
  searchId?: string | number;
}

/** La fecha del siniestro queda nula hasta que se elige una. */
export type CampoFecha = CampoSiniestro<Date | string | null>;

/**
 * Las tarjetas compartidas declaran un valor que puede venir nulo y un `focus`
 * opcional, mientras que el estado guarda siempre un texto y un `focus`
 * definido. La conversión se hace acá, al entrar al estado, para que ningún
 * campo quede con una forma distinta al resto.
 */
export const aCampoSiniestro = (
  event: FieldChangeEvent<string | null>
): CampoSiniestro => ({
  value: event.value ?? "",
  valid: event.valid,
  focus: event.focus ?? false,
});

/** La misma conversión para la tarjeta de fecha, que sí admite valor nulo. */
export const aCampoFecha = (
  event: FieldChangeEvent<string | Date | null>
): CampoFecha => ({
  value: event.value,
  valid: event.valid,
  focus: event.focus ?? false,
});

/** Un tercero involucrado en el siniestro, mientras se lo está cargando. */
export interface Tercero {
  nombre: CampoSiniestro;
  apellido: CampoSiniestro;
  numeroDocumento: CampoSiniestro;
  compania: CampoSiniestro;
  numeroPoliza: CampoSiniestro;
  telefono: CampoSiniestro;
  dominio: CampoSiniestro;
  marca: CampoSiniestro;
  modelo: CampoSiniestro;
  danios: CampoSiniestro;
}

/**
 * Opción de los selectores de ubicación y del buscador de pólizas. Coincide con
 * el `ListItem` que esperan las tarjetas compartidas.
 */
export type OpcionSiniestro = { label: string; value: string | null };

/** La póliza vinculada se guarda tal como la devuelve el backend. */
export type PolizaVinculada = GetPolizaCargaPublicaResponse;

/**
 * Lo que el buscador necesita de cada póliza encontrada. La pantalla interna
 * busca contra el endpoint de pólizas, que devuelve bastante más que esto, pero
 * sólo se usan estos campos para armar la etiqueta del selector.
 */
export interface PolizaBuscada {
  id: string;
  estado: string | null;
  marca: string | null;
  modelo: string | null;
  dominio: string | null;
}

/** Respuesta del buscador de pólizas, recortada a lo que usa la pantalla. */
export interface PolizasBuscadas {
  polizas: PolizaBuscada[];
}

/**
 * Estado del formulario de carga, igual en la pantalla pública y en la interna.
 *
 * `polizaVinculada` es lo que decide el modo de la pantalla: mientras es null
 * los datos se cargan a mano, y cuando se elige una póliza pasan a ser de solo
 * lectura.
 */
export interface CargaSiniestroState {
  tipoSelected: CampoSiniestro;
  companiaSelected: CampoSiniestro;
  numeroSiniestroCompaniaSelected: CampoSiniestro;
  numeroPolizaSelected: CampoSiniestro;
  idPolizaSelected: CampoSiniestro;
  sucursalSelected: CampoSiniestro;
  oficinaIdSelected: CampoSiniestro;
  fechaSelected: CampoFecha;
  horaSelected: CampoSiniestro;
  direccionSelected: CampoSiniestro;
  localidadSelected: CampoSiniestro;
  provinciaSelected: CampoProvincia;
  codigoPostalSelected: CampoSiniestro;
  lesionesFisicasSelected: CampoSiniestro;
  comoOcurrioSelected: CampoSiniestro;
  daniosVehiculoSelected: CampoSiniestro;
  nombreConductorSelected: CampoSiniestro;
  apellidoConductorSelected: CampoSiniestro;
  numeroDocumentoConductorSelected: CampoSiniestro;
  dominioSelected: CampoSiniestro;
  modeloAnioSelected: CampoSiniestro;
  marcaSelected: CampoSiniestro;
  modeloSelected: CampoSiniestro;
  terceros: Tercero[];
  numeroDocumentoAseguradoSelected: CampoSiniestro;
  nombreAseguradoSelected: CampoSiniestro;
  mailSelected: CampoSiniestro;
  telefonoSelected: CampoSiniestro;
  documentosSelected: string[];
  localidades: OpcionSiniestro[];
  codigosPostales: OpcionSiniestro[];
  polizaVinculada: PolizaVinculada | null;
  polizasFilter: OpcionSiniestro[];
  submitting: boolean;
  isLoading: boolean;
  isLoadingPoliza: boolean;
  creatingSiniestro: boolean;
  siniestroCreado: boolean;
  isProcessingDocumentos: boolean;
  failed?: boolean;
  reloadPolizas?: boolean;
}

/**
 * Datos que el formulario arma al enviar. Los campos pierden acá el andamiaje
 * de validación: viaja el valor solo.
 *
 * `numeroSiniestroCompania` no está: la pantalla lo mantiene en el estado pero
 * nunca lo manda, y el backend lo acepta vacío.
 */
export interface CargaSiniestroData {
  tipo: string;
  compania: string;
  numeroPoliza: string;
  idPoliza: string;
  sucursal: string;
  oficinaId: string;
  fechaSiniestro: Date | string | null;
  hora: string;
  direccion: string;
  provincia: string;
  localidad: string;
  codigoPostal: string;
  lesionesFisicas: string;
  comoOcurrio: string;
  daniosVehiculoAsegurado: string;
  numeroDocumentoAsegurado: string;
  nombreAsegurado: string;
  email: string;
  telefono: string;
  dominio: string;
  modeloAnio: string;
  marca: string;
  modelo: string;
  nombreConductor: string;
  apellidoConductor: string;
  numeroDocumentoConductor: string;
  terceros: TerceroData[];
  documentacion: string[];
}

/** Un tercero tal como viaja al backend. */
export interface TerceroData {
  nombre: string;
  apellido: string;
  numeroDocumento: string;
  compania: string;
  numeroPoliza: string;
  telefono: string;
  dominio: string;
  marca: string;
  modelo: string;
  danios: string;
}

/**
 * Criterios con los que el buscador pide pólizas. La pantalla sólo completa
 * `searchCriteria` y `estado`; el resto viaja vacío desde que se reusó la
 * búsqueda del listado de pólizas.
 */
export interface GetPolizasCriteria {
  skip: number;
  take: number;
  fechaDesde?: Date | null;
  fechaHasta?: Date | null;
  searchCriteria?: string;
  negocio?: string;
  estado?: string;
  soloParaRenovar?: boolean;
  compania?: string;
}

/** Evento que emite el uploader múltiple al agregar archivos. */
export interface ArchivosAgregadosEvent {
  target: { value: string[] };
}

/** Evento que emite el uploader múltiple al quitar un archivo. */
export interface ArchivoQuitadoEvent {
  target: { value: string | null };
}

/** Servicios que consume el formulario público. */
export interface ServicesCargaPublica {
  api: Pick<ApiServices, "siniestrosCargaPublica" | "provincias">;
}

/**
 * Servicios que consume el formulario interno.
 *
 * `polizas` se declara acá en vez de tomarse de `ApiServices` porque sus tipos
 * todavía describen la respuesta en PascalCase, y lo que llega es camelCase.
 * Esto es lo que el endpoint devuelve de verdad, recortado a lo que la pantalla
 * usa; cuando se corrijan los tipos de pólizas, este bloque se borra y vuelve a
 * alcanzar con el `Pick`.
 */
export interface ServicesCargaSiniestro {
  api: Pick<ApiServices, "siniestros" | "provincias"> & {
    polizas: () => {
      getPoliza: (id: string) => Promise<PolizaVinculada>;
      getPolizas: (data: GetPolizasApiRequest) => Promise<PolizasBuscadas>;
    };
  };
}

/** Servicios que consume el detalle de un siniestro. */
export interface ServicesDetalleSiniestro {
  api: Pick<ApiServices, "siniestros">;
}

/**
 * Estado del detalle. `comentarioNuevo` queda en true después de guardar un
 * comentario: abre el aviso de éxito y hace que el contenedor vuelva a pedir
 * el siniestro.
 */
export interface DetalleSiniestroState {
  isLoading: boolean;
  currentSiniestro: GetSiniestroResponse | null;
  failed: boolean;
  comentarioNuevo: boolean;
}

/** Las tarjetas del detalle sólo reciben si el siniestro se está cargando. */
export interface TarjetaDetalleSiniestroProps {
  isLoading: boolean;
}

/**
 * Lo que las tarjetas leen del detalle. Mientras no llegó el siniestro leen un
 * objeto vacío, así que cualquier campo puede faltar.
 */
export type SliceDetalleSiniestro = Partial<GetSiniestroResponse>;

/**
 * Lo que el buscador le manda al endpoint de pólizas. `riesgoId` viaja vacío:
 * el endpoint lo declara pero no lo usa para filtrar.
 */
export interface GetPolizasApiRequest {
  skip: number;
  take: number;
  riesgoId: string;
  fechaDesde?: Date;
  fechaHasta?: Date;
  searchCriteria: string;
  negocio: string;
  estado: string;
  soloParaRenovar: boolean;
  compania: string;
}

/** Servicios que consume el listado. */
export interface ServicesListadoSiniestros {
  api: Pick<ApiServices, "siniestros">;
}

/** Servicios que consume la descarga del reporte desde los filtros. */
export interface ServicesReportesSiniestros {
  api: Pick<ApiServices, "reportes">;
}

/** Valor de un filtro del listado: los selectores entregan texto o número. */
export type FiltroValue = string | number;

/**
 * Fecha de un filtro del listado: arranca como texto vacío y pasa a `Date` al
 * elegirla, sea desde la pantalla o desde la URL.
 */
export type FechaFiltro = Date | string | null;

/**
 * Los pedidos al API solo aceptan `Date`. El texto vacío inicial equivale a no
 * filtrar por fecha, que es como el API ya lo trataba.
 */
export const aFechaRequest = (fecha: FechaFiltro): Date | undefined =>
  fecha instanceof Date ? fecha : undefined;

/** Firma del `dispatch` que reciben los thunks de estas pantallas. */
export type Dispatch = (action: any) => void;

export type GetState = () => any;
